import { useState, useEffect, useRef } from "react";
import {
  Autocomplete,
  DatePicker,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Checkbox,
  FormControlLabel,
} from "../../../../components";
import {
  ColecoesService,
  TagsService,
  GrupoProdutosService,
  MarcasService,
  ProdutosService,
  SessionService,
} from "../../../../services";
import { value } from "../../../../helpers";
import { responseErros } from "../../../../utils";
import { useFiltrosRelatoriosHandlers } from "../../components/use-filtros-relatorios-handlers";

const colecoesService = new ColecoesService();
const marcasService = new MarcasService();
const produtoService = new ProdutosService();
const tagsService = new TagsService();
const grupoProdutosService = new GrupoProdutosService();

const FormFiltroAvancado = ({
  filtrosAvancados,
  setFiltrosAvancados,
  filtrosAplicadosImpressao,
  setFiltrosAplicadosImpressao,
  setValidationError
}) => {
  const [grupoProdutoList, setGrupoProdutoList] = useState([]);
  const [listaProdutos, setListaProdutos] = useState([]);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);
  const [tags, setTags] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [colecoes, setColecoes] = useState([]);
  const { onChangeInput, onChangeAutocomplete, onChangeDate, onChangeCheckbox } =
    useFiltrosRelatoriosHandlers(
      setFiltrosAvancados,
      setFiltrosAplicadosImpressao
    );

  const inputRef = useRef();
  const sessionService = new SessionService();
  const usuario = sessionService.getUsuario();
  const empresa = sessionService.getEmpresa();
  const listaTipos = [
    {
      label: "Simples",
      value: "simples",
    },
    {
      label: "Grade",
      value: "grade",
    },
    {
      label: "Kit",
      value: "kit",
    },
  ];

  useEffect(() => {
    setEstadoInicial();

    if (filtrosAvancados?.produtoId) {
      buscarProdutos({ id: filtrosAvancados.produtoId });
    }
    buscarGruposProduto();
    buscarMarcas();
    buscarColecoes();
    buscarTags();
  }, []);

  const buscarGruposProduto = () => {
    const filtros = {
      attributes: ['id', 'codigo', 'descricao'],
      disabledInclude: true,
      nonPaginated: true,
    };
    grupoProdutosService.getAll(filtros).then((result) => {
      if (!result.isAxiosError) {
        setGrupoProdutoList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const onInputChangeProdutos = (event) => {
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      const eventValue = event.target?.value;
      if (eventValue?.length >= 2) {
        const filtros = {
          codigo: eventValue,
          codigoBarra: eventValue,
          descricao: eventValue,
        };
        setLoadingAutoComplete(true);
        buscarProdutos(filtros);
      }
    }
  };

  const onChangeMultiple = (valores, name) => {
    if (!valores?.length) {
      resetarFiltros(name);
      return;
    }
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      [name]: valores.map(({ id }) => id),
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      [formatChaveFiltroImpressao(name)]: formatValorFiltroImpressao(
        valores,
        name
      ),
    }));
  };

  function resetarFiltros(name) {
    setFiltrosAvancados((prevState) => {
      const prevStateTemp = { ...prevState };
      delete prevStateTemp[name];
      return prevStateTemp;
    });

    setFiltrosAplicadosImpressao((prevState) => {
      const prevStateTemp = { ...prevState };
      delete prevStateTemp[formatChaveFiltroImpressao(name)];
      return prevStateTemp;
    });
  }

  function formatChaveFiltroImpressao(name) {
    return name.endsWith("Id") ? name.slice(0, -2).toUpperCase() : name;
  }

  function formatValorFiltroImpressao(valores, name) {
    switch (name) {
      case "tagsId":
        return valores.map((valor) => valor.descricao);
      case "marcasId":
        return valores.map((valor) => valor.nome);
      case "colecoesId":
        return valores.map((valor) => valor.descricao);
      default:
        return valores;
    }
  }

  function setEstadoInicial() {
    const filtros = {
      ...filtrosAvancados,
      empresasIds: [empresa.id],
    };
    let filtrosImpressao = {};
    if (usuario.acessosEmpresas?.length) {
      filtrosImpressao = {
        ...filtrosAplicadosImpressao,
        ["Empresas-Associadas"]: empresa.razaoSocial,
      };
    }

    setFiltrosAvancados(filtros);
    setFiltrosAplicadosImpressao(filtrosImpressao);
  }

  const buscarProdutos = (filtros) => {
    const filtro = {
      ...filtros,
      ativado: true,
      limite: 20,
    };
    produtoService.getAllFiltroAvancado(filtro).then((result) => {
      setLoadingAutoComplete(false);
      if (!result.isAxiosError) {
        setListaProdutos(result.data.rows);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarTags = async function () {
    const filtros = {
      nonPaginated: true,
      ativado: true,
      tipo: "PRODUTO",
    };
    const result = await tagsService.getAll(filtros);
    if (!result.isAxiosError) {
      setTags(result.data);
    } else {
      responseErros(result);
    }
  };

  const buscarMarcas = async function (filter) {
    const result = await marcasService.getAll(filter);
    if (!result.isAxiosError) {
      setMarcas(result.data.rows);
    } else {
      responseErros(result);
    }
  };

  const buscarColecoes = async function (filter) {
    const result = await colecoesService.getAll(filter);
    if (!result.isAxiosError) {
      setColecoes(result.data.rows);
    } else {
      responseErros(result);
    }
  };

  const getMultipleAutocompleteValues = (valores, lista) => {
    if (valores) {
      return valores.map((valor) => lista.find((item) => item.id === valor));
    }
    return [];
  };

  function onChangeMultipleEmpresas(empresas) {
    if (empresas.length === 0) {
      return;
    }
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      empresasIds: empresas.map(({ id }) => id),
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      "Empresas-Associadas": empresas
        .map(({ razaoSocial }) => razaoSocial)
        .join(" | "),
    }));
  }

  return (
    <>
      {usuario.acessosEmpresas?.length > 0 && (
        <>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              id="empresasIds"
              name="empresasIds"
              options={usuario.acessosEmpresas}
              noOptionsText="Sem opções"
              autoHighlight
              disablePortal
              filterSelectedOptions
              getOptionLabel={(option) => option?.razaoSocial || ""}
              onChange={(_, newValue) => onChangeMultipleEmpresas(newValue)}
              value={value.multiple(
                usuario.acessosEmpresas || [],
                filtrosAvancados["empresasIds"]
                  ? filtrosAvancados["empresasIds"]
                  : ""
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  required
                  label="Empresas"
                  margin="normal"
                />
              )}
            />
          </Grid>
        </Grid>
        <Divider sx={{mt: 2, mb: 3, border: "1px solid grey" }}/>
        </>
      )}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataStart"
            name="Data-Start"
            label="Data Inicial"
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataStart)}
            onChange={(date) => onChangeDate("dataStart", date, "Data-Start")}
            slotProps={{
              popper: { sx: { zIndex: 2000 }, }
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataEnd"
            name="Data-End"
            label="Data Final"
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataEnd)}
            onChange={(date) => onChangeDate("dataEnd", date, "Data-End")}
            slotProps={{
              popper: { sx: { zIndex: 2000 }, }
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="grupoProdutoId"
              name="grupoProdutoId"
              options={grupoProdutoList}
              noOptionsText="Sem opções"
              autoHighlight
              disablePortal
              getOptionLabel={(option) => (option ? option.descricao : "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Grupo de Produto"
                  variant="outlined"
                />
              )}
              onChange={(_, newValue) => {
                onChangeAutocomplete(
                  "grupoProdutoId",
                  newValue,
                  "Grupo de Produtos"
                );
              }}
              value={value.autoComplete(grupoProdutoList, filtrosAvancados?.grupoProdutoId)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            id="produtoId"
            name="produtoId"
            options={listaProdutos}
            autoHighlight
            disablePortal
            loading={loadingAutoComplete}
            loadingText="Carregando"
            noOptionsText="Digite e pressione Enter"
            getOptionLabel={(option) =>
              option.descricao ? `${option?.codigo} - ${option?.descricao}` : ""
            }
            filterSelectedOptions
            onChange={(_, newValue) =>
              onChangeAutocomplete("produtoId", newValue, "Produto")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant={"outlined"}
                label="Produto"
                margin="normal"
                onKeyDown={(e) => onInputChangeProdutos(e)}
                inputRef={inputRef}
                InputProps={{
                  ...params.InputProps,
                }}
              />
            )}
            value={value.autoComplete(
              listaProdutos,
              filtrosAvancados.produtoId
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Select
            label="Tipo"
            id="tipo"
            name="tipo"
            margin="normal"
            variant="outlined"
            value={value.text(filtrosAvancados?.tipo)}
            onChange={(event) => onChangeInput(event, "Tipo")}
            SelectProps={{
              MenuProps: {
                disablePortal: true,
                PaperProps: {
                  sx: {
                    zIndex: 1600, 
                  },
                },
              },
            }}
          >
            {listaTipos.map((tipo) => (
              <MenuItem key={tipo.value} value={tipo.value}>
                {tipo.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            multiple
            id="tagsId"
            name="tagsId"
            options={tags}
            noOptionsText="Sem opções"
            autoHighlight
            disablePortal
            filterSelectedOptions
            getOptionLabel={(option) => (option ? option.descricao : "")}
            renderOption={(props, option) =>
              option ? (
                <li {...props}>
                  <div className="d-flex align-items-center gap-2 w-100">
                    <i
                      className="ph-fill ph-bookmark-simple"
                      style={{ color: option.cor, fontSize: 18 }}
                    />
                    <div>{option.descricao}</div>
                  </div>
                </li>
              ) : (
                ""
              )
            }
            onChange={(_, newValue) => onChangeMultiple(newValue, "tagsId")}
            value={getMultipleAutocompleteValues(
              filtrosAvancados?.tagsId,
              tags
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                margin="normal"
                label="Tags"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            multiple
            id="marcasId"
            name="marcasId"
            options={marcas}
            noOptionsText="Sem opções"
            autoHighlight
            disablePortal
            filterSelectedOptions
            getOptionLabel={(option) => option?.nome || ""}
            onChange={(_, newValue) => onChangeMultiple(newValue, "marcasId")}
            value={getMultipleAutocompleteValues(
              filtrosAvancados?.marcasId,
              marcas
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                margin="normal"
                label="Marcas"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            multiple
            id="colecoesId"
            name="colecoesId"
            options={colecoes}
            noOptionsText="Sem opções"
            autoHighlight
            disablePortal
            filterSelectedOptions
            getOptionLabel={(option) => option?.descricao || ""}
            onChange={(_, newValue) => onChangeMultiple(newValue, "colecoesId")}
            value={getMultipleAutocompleteValues(
              filtrosAvancados?.colecoesId,
              colecoes
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                margin="normal"
                label="Coleções"
              />
            )}
          />
        </Grid>
      </Grid>
      {empresa?.moduloServico && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              label="Considerar Ordem de Serviço"
              labelPlacement="end"
              control={
                <Checkbox
                  name="ordemServico"
                  checked={filtrosAvancados?.ordemServico || false}
                  onChange={(event) => onChangeCheckbox(event, "Ordem-Servico")}
                  color="primary"
                />
              }
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default FormFiltroAvancado;
